// Frontend messaging service
import { functions } from '../plugins/firebase'
import { Notify } from 'quasar'
import axios from 'axios'
import { useAuthStore } from '../stores/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'

class MessagingService {
  constructor() {
    this.sendMessage = httpsCallable(functions, "sendMessage"); // Use already initialized `functions`
  }

  /**
   * Sends a message via a specific channel.
   * @param {string} recipientId - The recipient's ID.
   * @param {string} message - The message content.
   * @param {string} channel - The channel to send the message (e.g., SMS, MMS, WhatsApp, RCS).
   * @param {object} metadata - Additional metadata (optional).
   * @returns {Promise<object>} - Result of the message sending.
   * @throws {Error} - If the message sending fails.
   */
  async send(recipientId, message, channel, metadata = {}) {
    try {
      const authStore = useAuthStore();
      const user = authStore.user;

      if (!user || !user.businessId) {
        throw new Error("User is not authenticated or missing business ID.");
      }

      const payload = {
        recipientId,
        message,
        channel,
        metadata,
        businessId: user.businessId,
        resellerId: user.resellerId || null,
      };

      const result = await this.sendMessage(payload);

      Notify.create({
        type: 'positive',
        message: `Message sent successfully via ${channel}.`,
        position: 'top-right',
      });

      return result.data;
    } catch (error) {
      console.error('Error sending message:', error);

      Notify.create({
        type: 'negative',
        message: `Failed to send message via ${channel}. Please try again.`,
        position: 'top-right',
      });

      throw error;
    }
  }

  /**
   * Sends an SMS.
   * @param {string} recipientId - The recipient's ID.
   * @param {string} message - The SMS content.
   */
  async sendSMS(to, message) {
    try {
      const response = await axios.post(`${this.apiUrl}/sendSMS`, { to, message });
      Notify.create({ type: "positive", message: "SMS sent successfully!", position: "top-right" });
      return response.data;
    } catch (error) {
      console.error("Error sending SMS:", error);
      Notify.create({ type: "negative", message: "Failed to send SMS.", position: "top-right" });
      throw error;
    }
  }

  async sendMessage(to, message, businessId, resellerId, channel) {
    try {
      const response = await axios.post(`${this.apiUrl}/send${channel}`, {
        to,
        message,
        businessId,
        resellerId,
      });

      Notify.create({
        type: "positive",
        message: `${channel} message sent successfully!`,
        position: "top-right",
      });

      return response.data;
    } catch (error) {
      console.error(`Error sending ${channel} message:`, error);
      Notify.create({
        type: "negative",
        message: `Failed to send ${channel} message.`,
        position: "top-right",
      });
      throw error;
    }
  }

  async fetchMessages(collection, status) {
    try {
      const user = authStore.user;
      if (!user || !user.businessId) {
        throw new Error("User is not authenticated or missing business ID.");
      }

      const response = await axios.get(`${this.apiUrl}/getMessages`, {
        params: { collection, businessId: user.businessId, status },
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching messages:`, error);
      throw error;
    }
  }

  /**
   * Update message status (read/clicked)
   */
  async updateMessageStatus(messageId, statusType) {
    try {
      const response = await axios.post(`${this.apiUrl}/updateMessageStatus`, {
        messageId,
        statusType,
      });

      Notify.create({
        type: "positive",
        message: `Message marked as ${statusType}`,
        position: "top-right",
      });

      return response.data;
    } catch (error) {
      console.error("Error updating message status:", error);
      throw error;
    }
  }

  async getChatHistory(userId) {
    try {
      const response = await axios.get(`${this.apiUrl}/getChatHistory`, {
        params: { userId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching chat history:", error);
      throw error;
    }
  }

  /**
   * Sends an MMS with media attachments.
   * @param {string} recipientId - The recipient's ID.
   * @param {string} message - The MMS content.
   * @param {array} media - Media attachments (URLs or files).
   */
  async sendMMS(recipientId, message, media) {
    return this.send(recipientId, message, 'mms', { media })
  }

  /**
   * Sends a message via WhatsApp.
   * @param {string} recipientId - The recipient's ID.
   * @param {string} message - The WhatsApp message content.
   * @param {string} template - Template used for the WhatsApp message (optional).
   */
  async sendWhatsApp(recipientId, message, template) {
    return this.send(recipientId, message, 'whatsapp', { template })
  }

  /**
   * Sends a message via RCS.
   * @param {string} recipientId - The recipient's ID.
   * @param {string} message - The RCS message content.
   * @param {array} suggestions - Suggested actions (optional).
   */
  async sendRCS(recipientId, message, suggestions) {
    return this.send(recipientId, message, 'rcs', { suggestions })
  }
}

export const messagingService = new MessagingService()